import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the Integrity screen (task 11.9; design 11, 12.2 Integrity row).
 *
 * Three operator-API operations back the screen, all role `viewer`, taken straight from the
 * generated `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/integrity/core` → {@link CoreIntegrityPage}: the Protected_Core attestation state
 *   per instance (service, `state`, `failure_reason`, `reported_at`, `stale`). This is the PRIMARY
 *   snapshot the screen's top-level state rests on.
 * - `GET /api/v1/integrity/audit-chains` → {@link AuditChainPage}: one page of audit chains. By
 *   contract EVERY chain always carries `verification_level` (the string `pending` while the level
 *   is undetermined), `head`, `export_lag` and `first_failure` (the string `none` when there is
 *   none) — see {@link AuditChain}. The screen renders all four on every row; it never presents a
 *   chain as verified without its level (FR-6.8).
 * - `GET /api/v1/integrity/audit-chains/{id}` → {@link AuditChainDetail}: one chain WITH a page of
 *   its batches, newest first. Used by the drill-down from an audit-chain row.
 *
 * These operations are SNAPSHOTS with NO `from`/`to` window — unlike the Overview/Decisions/Identity
 * reads, the integrity endpoints describe the current attestation/verification state, not a span
 * (openapi.yaml: the operations take only `cursor`/`limit`/filters, never `From`/`To`). So there is
 * no window to resolve. The shared time-range's live/pause flag is still honoured for the ONE thing
 * it can mean here: `live` drives whether we poll for a fresh snapshot (so a growing export lag is
 * seen) and pausing FREEZES the polling, exactly as pausing freezes the windowed screens (FR-13
 * AC2). We do not fabricate a `from`/`to` the contract does not accept.
 *
 * Fetches go through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} and
 * the shell shows "Waking the control plane"; queries are retried per `query-client.ts`.
 */

/** The contract `CoreIntegrityPage` response body (Protected_Core state per instance). */
export type CoreIntegrityPage = components["schemas"]["CoreIntegrityPage"];
/** A single Protected_Core instance row. */
export type CoreIntegrityRow = CoreIntegrityPage["items"][number];
/** The contract `AuditChainPage` response body (one page of audit chains). */
export type AuditChainPage = components["schemas"]["AuditChainPage"];
/** A single audit chain, with the four always-present members. */
export type AuditChain = components["schemas"]["AuditChain"];
/** A single pinned/observed audit key on a chain. */
export type AuditKeyPin = components["schemas"]["AuditKeyPin"];
/** The contract `AuditChainDetail` response body (a chain with a page of its batches). */
export type AuditChainDetail = components["schemas"]["AuditChainDetail"];
/** A single batch record in a chain's detail. */
export type AuditBatchRecord = components["schemas"]["AuditBatchRecord"];

export interface UseCoreIntegrityResult {
  readonly query: UseQueryResult<CoreIntegrityPage, ApiError>;
}

export interface UseAuditChainsResult {
  readonly query: UseQueryResult<AuditChainPage, ApiError>;
}

export interface UseAuditChainResult {
  readonly query: UseQueryResult<AuditChainDetail, ApiError>;
}

/**
 * Fetch the Protected_Core state for every instance. A snapshot, so the query key carries no
 * window; `live` drives whether we re-poll (and pausing freezes it). `staleTime` tolerates a little
 * staleness while live, and never goes stale while paused.
 */
export function useCoreIntegrity(): UseCoreIntegrityResult {
  const timeRange = useTimeRange();

  const query = useQuery<CoreIntegrityPage, ApiError>({
    queryKey: ["integrity-core"],
    queryFn: ({ signal }) => apiFetch<CoreIntegrityPage>("/integrity/core", { signal }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
  });

  return { query };
}

/**
 * Fetch the first page of audit chains. A snapshot (no window); `live` drives the polling so a
 * growing export lag is picked up, and pausing freezes it. The first page only — `next_cursor` is
 * left for a later pagination affordance, matching the identity lifecycle screen.
 */
export function useAuditChains(): UseAuditChainsResult {
  const timeRange = useTimeRange();

  const query = useQuery<AuditChainPage, ApiError>({
    queryKey: ["integrity-audit-chains"],
    queryFn: ({ signal }) => apiFetch<AuditChainPage>("/integrity/audit-chains", { signal }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
  });

  return { query };
}

/**
 * Fetch one audit chain with the first page of its batches (newest first). Enabled only when a
 * chain id is supplied (the drill-down expands a row), so no request fires until the operator opens
 * a chain. `live` drives the polling and pausing freezes it, as above.
 */
export function useAuditChain(id: string | null): UseAuditChainResult {
  const timeRange = useTimeRange();

  const query = useQuery<AuditChainDetail, ApiError>({
    queryKey: ["integrity-audit-chain", id],
    queryFn: ({ signal }) =>
      apiFetch<AuditChainDetail>(`/integrity/audit-chains/${encodeURIComponent(id as string)}`, {
        signal,
      }),
    enabled: id !== null,
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
  });

  return { query };
}
